/**
 * Consultant Profiles API Client
 *
 * GW-3056: Client for gw-services-api consultant profile endpoints.
 * Base URL: services.{env}.generalwisdom.com
 * All requests include Accept-Version: 1.0 header via ServicesApiClient.
 */

import type {
  ConsultantProfile,
  ConsultantProfileListResponse,
  UpdateConsultantProfileRequest,
} from '@/types/consultant-profile.types';
// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.
import { assertOrgId } from '@/types/orgId';

import { ServicesApiClient } from './servicesApiClient';

class ConsultantProfilesAPI extends ServicesApiClient {
  /**
   * Get a consultant profile by user ID.
   * GET /consultants/:userId
   */
  async getProfile(userId: string): Promise<ConsultantProfile> {
    return this.get<ConsultantProfile>(`/consultants/${userId}`);
  }

  /**
   * Update the authenticated consultant's own profile.
   * PUT /consultants/profile
   */
  async updateProfile(data: UpdateConsultantProfileRequest): Promise<ConsultantProfile> {
    return this.put<ConsultantProfile>('/consultants/profile', data);
  }

  /**
   * Request a presigned URL to upload a consultant avatar.
   * POST /consultants/profile/avatar
   */
  async uploadAvatar(): Promise<{ uploadUrl: string; expiresAt: string }> {
    return this.post<{ uploadUrl: string; expiresAt: string }>('/consultants/profile/avatar');
  }

  /**
   * Set the anonymous visibility of a consultant within an organization.
   * PUT /organizations/:orgId/consultants/:userId/visibility
   */
  async setVisibility(
    orgId: string,
    userId: string,
    isAnonymous: boolean
  ): Promise<ConsultantProfile> {
    assertOrgId(orgId);
    return this.put<ConsultantProfile>(`/organizations/${orgId}/consultants/${userId}/visibility`, {
      isAnonymous,
    });
  }

  /**
   * List consultants for an organization.
   * GET /organizations/:orgId/consultants
   */
  async listOrgConsultants(
    orgId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<ConsultantProfileListResponse> {
    assertOrgId(orgId);
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<ConsultantProfileListResponse>(`/organizations/${orgId}/consultants${qs}`);
  }
}

export const consultantProfilesApi = new ConsultantProfilesAPI();
